import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import {
	ProductsService,
	type IGetProductsParams,
} from '@services/products.service';

// --- Stores ---
import { ProductsStore } from '@stores/products.store';

// --- Facades ---
import { ParametersFacade } from './parameters.facade';

// --- Utils ---
import { simpleDiff } from '@utils/simple-diff.util';

// --- Interfaces ---
import type { AttachFileData } from '@interfaces/file';
import type { IProduct, IVideo } from '@interfaces/product';

const REFRESH_INTERVAL = 120 * 1000; // 1 min 30sec

@Injectable({
	providedIn: 'root',
})
export class ProductsFacade {
	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);
	readonly refresh$ = this._refresh.asObservable();

	private readonly _filter = new BehaviorSubject<IGetProductsParams>({});
	readonly filter$ = this._filter.asObservable().pipe(
		debounceTime(300),
		distinctUntilChanged((prev, curr) => !simpleDiff(prev, curr))
	);

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this.autoRefresh$,
		this.refresh$,
	]).pipe(switchMap(([filter]) => this.getProducts(filter)));

	private readonly handleRequestSimplified$ = combineLatest([
		this.filter$,
		this.autoRefresh$,
		this.refresh$,
	]).pipe(switchMap(([filter]) => this.getSimplifiedProducts(filter)));

	readonly simplifiedProductsState$ = combineLatest([
		this.productsStore.productsState$,
		this.handleRequestSimplified$,
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	readonly productsState$ = combineLatest([
		this.productsStore.productsState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	constructor(
		private parametersFacade: ParametersFacade,
		private productsService: ProductsService,
		private productsStore: ProductsStore
	) {}

	private get showOutOfStock() {
		return this.parametersFacade?.parameters?.showProductsWithoutStock || false;
	}

	getProducts(queryParams?: IGetProductsParams) {
		return this.productsService
			.getProducts({ ...queryParams, showOutOfStock: this.showOutOfStock })
			.pipe(
				tap(products => this.productsStore.updateProducts(products)),
				shareReplay()
			);
	}

	getSimplifiedProducts(queryParams?: IGetProductsParams) {
		return this.productsService
			.getSimplifiedProducts({
				...queryParams,
				showOutOfStock: this.showOutOfStock,
			})
			.pipe(
				tap(products => this.productsStore.updateProducts(products)),
				shareReplay()
			);
	}

	getProductsSearch(queryParams?: IGetProductsParams) {
		return this.productsService.getProducts({
			...queryParams,
			showOutOfStock: this.showOutOfStock,
		});
	}

	getProductsByCategory(queryParams?: IGetProductsParams) {
		return this.productsService.getProductsByCategory(queryParams);
	}

	getProductById(id: IProduct['id'], isEditing?: boolean) {
		return this.productsService
			.getProductById(id, isEditing)
			.pipe(map(produto => this.formatProductCategories(produto)));
	}

	newProduct(product: IProduct) {
		return this.productsService
			.postProduct(product)
			.pipe(tap(product => this.productsStore.updateProduct(product)));
	}

	updateProduct(product: IProduct) {
		return this.productsService
			.updateProduct(product)
			.pipe(tap(product => this.productsStore.updateProduct(product)));
	}

	updateSale(id: IProduct['id'], sale: any) {
		return this.productsService
			.updateSale(id, sale)
			.pipe(tap(product => this.productsStore.updateProduct(product)));
	}

	deleteProduct(product: IProduct) {
		return this.productsService
			.deleteProduct(product.id)
			.pipe(tap(() => this.productsStore.deleteProduct(product)));
	}

	activateProduct(product: IProduct) {
		return this.productsService
			.activate(product.id)
			.pipe(tap(() => this.productsStore.deleteProduct(product)));
	}

	postProductPicture(id: IProduct['id'], file: AttachFileData, data: any) {
		return this.productsService.postProductPicture(id, file, data);
	}

	savePicture(productId: IProduct['id'], pictureId: number) {
		return this.productsService.savePicture(productId, pictureId);
	}

	deletePicture(pictureId: number) {
		return this.productsService.deletePicture(pictureId);
	}

	deletePrice(productId: IProduct['id'], priceId: number) {
		return this.productsService.deletePrice(productId, priceId);
	}

	deleteStock(productId: IProduct['id'], stockId: number) {
		return this.productsService.deleteStock(productId, stockId);
	}

	postVideo(id: IProduct['id'], video: IVideo) {
		return this.productsService.postVideo(id, video);
	}

	putVideo(productId: IProduct['id'], videoId: number, video: IVideo) {
		return this.productsService.putVideo(productId, videoId, video);
	}

	deleteVideo(videoId: number) {
		return this.productsService.deleteVideo(videoId);
	}

	filterProducts(filter: IGetProductsParams) {
		delete filter.totalPages;
		this._filter.next(filter);
	}

	refresh() {
		this._refresh.next(undefined);
	}

	private formatProductCategories(produto: IProduct): IProduct {
		if (
			produto?.secondaryCategories &&
			Array.isArray(produto.secondaryCategories)
		) {
			produto.secondaryCategories = produto.secondaryCategories.map(cat => {
				if (cat.isDefault && cat.name.toLowerCase() === 'sem categoria') {
					return { ...cat, name: cat.name.toUpperCase() };
				}
				return cat;
			});
		}
		return produto;
	}
}
